import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  Camera,
  ClipboardList,
  LayoutDashboard,
  Paperclip,
  Pencil,
  Plus,
  Printer,
  Trash2,
  Truck,
  UserCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { DateInput } from "@/components/ui/DateInput";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { APP_SHORT_NAME } from "@/config/constants";
import { currentLocalDateInput } from "@/utils/dateInput";
import { formatDate, formatDateTime, formatNumber } from "@/utils/format";
import {
  CARRADA_MATERIAIS,
  APONTADOR_TIPO_LABELS,
  COMPARATIVO_CARRADA_LABELS,
  carradaFotoUrl,
  createApontador,
  createCarrada,
  deleteApontador,
  deleteCarrada,
  deleteFrequencia,
  fetchApontadores,
  fetchCarradasDashboard,
  fetchCarradasLancamentos,
  fetchCaminhoesCarradas,
  fetchContratosCarradas,
  fetchFrequenciaByData,
  fetchRelatorioCarradas,
  fetchRelatorioFrequencia,
  formatCpfDisplay,
  FREQUENCIA_SITUACAO_LABELS,
  FREQUENCIA_SITUACOES,
  saveFrequenciaLote,
  updateApontador,
  updateCarrada,
  type ApontadorDto,
  type ApontadorFormValues,
  type CaminhaoCarradasDto,
  type CarradaDto,
  type CarradaFormValues,
  type CarradasDashboardResumoDto,
  type ContratoCarradasDto,
  type FrequenciaSituacao,
  type RelatorioCarradasDto,
  type RelatorioFrequenciaDto,
} from "@/services/carradas";
import { extractApiError } from "@/services/locacoes";
import { cn } from "@/utils/cn";
import { CarradaFotoLightbox } from "./CarradaFotoLightbox";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { LocacoesSubToolbar } from "./LocacoesSubToolbar";
import { ModalApontador } from "./ModalApontador";
import { ModalCarrada } from "./ModalCarrada";
import {
  locacoesTabClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
  waitForPrintableImages,
} from "./locacoesUtils";
import { upperLocacaoText } from "./upperLocacaoText";

type CarradasSubTab = "dashboard" | "apontadores" | "frequencia" | "lancamentos" | "relatorio";

const SUB_TABS: Array<{ id: CarradasSubTab; label: string; icon: typeof LayoutDashboard }> = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "apontadores", label: "Apontadores", icon: Users },
  { id: "frequencia", label: "Frequência", icon: ClipboardList },
  { id: "lancamentos", label: "Carradas", icon: Truck },
  { id: "relatorio", label: "Relatório", icon: Printer },
];

interface FreqRow {
  apontadorId: string;
  nome: string;
  situacao: FrequenciaSituacao;
  valorDiaria: number | null;
  observacao: string;
}

interface ConfirmState {
  title: string;
  message: string;
  action: () => Promise<void>;
}

export function TabCarradas() {
  const [subTab, setSubTab] = useState<CarradasSubTab>("dashboard");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [resumo, setResumo] = useState<CarradasDashboardResumoDto | null>(null);
  const [apontadores, setApontadores] = useState<ApontadorDto[]>([]);
  const [apontadorBusca, setApontadorBusca] = useState("");
  const [editingApontador, setEditingApontador] = useState<ApontadorDto | null>(null);
  const [modalApontadorOpen, setModalApontadorOpen] = useState(false);

  const [freqData, setFreqData] = useState(currentLocalDateInput());
  const [freqRows, setFreqRows] = useState<FreqRow[]>([]);

  const [caminhoes, setCaminhoes] = useState<CaminhaoCarradasDto[]>([]);
  const [contratosCarradas, setContratosCarradas] = useState<ContratoCarradasDto[]>([]);
  const [lancamentos, setLancamentos] = useState<CarradaDto[]>([]);
  const [lancDataInicial, setLancDataInicial] = useState(currentLocalDateInput());
  const [lancDataFinal, setLancDataFinal] = useState(currentLocalDateInput());
  const [lancCaminhaoId, setLancCaminhaoId] = useState("");
  const [lancContratoId, setLancContratoId] = useState("");
  const [editingCarrada, setEditingCarrada] = useState<CarradaDto | null>(null);
  const [modalCarradaOpen, setModalCarradaOpen] = useState(false);
  const [fotoPreview, setFotoPreview] = useState<{
    items: Array<{ carradaId: string; fotoId: string }>;
    index: number;
  } | null>(null);

  const [relKind, setRelKind] = useState<"carradas" | "presenca">("carradas");
  const [relApontadorId, setRelApontadorId] = useState("");
  const [relDataInicial, setRelDataInicial] = useState(() => {
    const d = new Date();
    d.setDate(1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  });
  const [relDataFinal, setRelDataFinal] = useState(currentLocalDateInput());
  const [relCaminhaoId, setRelCaminhaoId] = useState("");
  const [relMaterial, setRelMaterial] = useState("");
  const [relatorio, setRelatorio] = useState<RelatorioFrequenciaDto | null>(null);
  const [relatorioCarradas, setRelatorioCarradas] = useState<RelatorioCarradasDto | null>(null);
  const [printGeneratedAt, setPrintGeneratedAt] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const caminhoesById = useMemo(() => new Map(caminhoes.map((c) => [c.id, c])), [caminhoes]);
  const contratosById = useMemo(
    () => new Map(contratosCarradas.map((c) => [c.id, c])),
    [contratosCarradas],
  );

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, aps, cams, contratos] = await Promise.all([
        fetchCarradasDashboard(),
        fetchApontadores(),
        fetchCaminhoesCarradas(),
        fetchContratosCarradas(),
      ]);
      setResumo(r);
      setApontadores(aps);
      setCaminhoes(cams);
      setContratosCarradas(contratos);
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar Carradas."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCore();
  }, [loadCore]);

  const loadFrequencia = useCallback(async (dataDia: string, lista: ApontadorDto[]) => {
    const ativos = lista.filter((a) => a.status === "ativo");
    const existentes = await fetchFrequenciaByData(dataDia);
    const byId = new Map(existentes.map((f) => [f.apontadorId, f]));
    setFreqRows(
      ativos.map((a) => {
        const ex = byId.get(a.id);
        return {
          apontadorId: a.id,
          nome: a.nome,
          situacao: ex?.situacao ?? "presente",
          valorDiaria: ex?.valorDiaria ?? null,
          observacao: upperLocacaoText(ex?.observacao ?? ""),
        };
      }),
    );
  }, []);

  useEffect(() => {
    if (subTab !== "frequencia" || apontadores.length === 0) return;
    void loadFrequencia(freqData, apontadores).catch((e) =>
      setError(extractApiError(e, "Erro ao carregar frequência.")),
    );
  }, [subTab, freqData, apontadores, loadFrequencia]);

  const loadLancamentos = useCallback(async () => {
    const list = await fetchCarradasLancamentos({
      dataInicial: lancDataInicial,
      dataFinal: lancDataFinal,
      ...(lancCaminhaoId ? { caminhaoId: lancCaminhaoId } : {}),
      ...(lancContratoId ? { contratoId: lancContratoId } : {}),
    });
    setLancamentos(list);
  }, [lancDataInicial, lancDataFinal, lancCaminhaoId, lancContratoId]);

  useEffect(() => {
    if (subTab !== "lancamentos") return;
    void loadLancamentos().catch((e) =>
      setError(extractApiError(e, "Erro ao carregar lançamentos.")),
    );
  }, [subTab, loadLancamentos]);

  const apontadoresFiltrados = useMemo(() => {
    const q = apontadorBusca.trim().toLowerCase();
    if (!q) return apontadores;
    const digits = q.replace(/\D/g, "");
    return apontadores.filter(
      (a) =>
        a.nome.toLowerCase().includes(q) ||
        (digits && a.cpf.includes(digits)) ||
        a.cpf.includes(q),
    );
  }, [apontadores, apontadorBusca]);

  const handleSaveApontador = async (values: ApontadorFormValues, editingId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      if (editingId) await updateApontador(editingId, values);
      else await createApontador(values);
      await loadCore();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteApontador = (a: ApontadorDto) => {
    setConfirm({
      title: "Excluir apontador",
      message: `Excluir "${a.nome}" e o histórico de frequência associado?`,
      action: async () => {
        setBusy(true);
        try {
          await deleteApontador(a.id);
          setModalApontadorOpen(false);
          setEditingApontador(null);
          await loadCore();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleSaveFrequencia = async () => {
    if (freqRows.length === 0) {
      setError("Não há apontadores ativos para lançar frequência.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveFrequenciaLote(
        freqData,
        freqRows.map((r) => ({
          apontadorId: r.apontadorId,
          situacao: r.situacao,
          valorDiaria: r.valorDiaria,
          observacao: r.observacao,
        })),
      );
      await loadCore();
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar frequência."));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveCarrada = async (
    values: CarradaFormValues,
    editingId: string | null,
  ): Promise<CarradaDto> => {
    setBusy(true);
    setError(null);
    try {
      const saved = editingId ? await updateCarrada(editingId, values) : await createCarrada(values);
      await loadCore();
      await loadLancamentos();
      return saved;
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCarrada = (c: CarradaDto) => {
    setConfirm({
      title: "Excluir carrada",
      message: "Remover este lançamento de carrada?",
      action: async () => {
        setBusy(true);
        try {
          await deleteCarrada(c.id);
          setModalCarradaOpen(false);
          setEditingCarrada(null);
          await loadCore();
          await loadLancamentos();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleGerarRelatorioPresenca = async () => {
    if (!relApontadorId) {
      setError("Selecione o apontador.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetchRelatorioFrequencia({
        apontadorId: relApontadorId,
        dataInicial: relDataInicial,
        dataFinal: relDataFinal,
      });
      setRelatorio(r);
      setPrintGeneratedAt(null);
    } catch (e) {
      setError(extractApiError(e, "Erro ao gerar relatório."));
    } finally {
      setBusy(false);
    }
  };

  const handleEditFrequencia = (frequencia: { data: string }) => {
    setFreqData(frequencia.data);
    setSubTab("frequencia");
  };

  const handleDeleteFrequencia = (frequencia: { id: string; data: string }) => {
    setConfirm({
      title: "Excluir frequência",
      message: `Excluir o registro de frequência de ${formatDate(frequencia.data)}?`,
      action: async () => {
        setBusy(true);
        setError(null);
        try {
          await deleteFrequencia(frequencia.id);
          if (relApontadorId && relDataInicial && relDataFinal) {
            const next = await fetchRelatorioFrequencia({
              apontadorId: relApontadorId,
              dataInicial: relDataInicial,
              dataFinal: relDataFinal,
            });
            setRelatorio(next);
          }
        } catch (e) {
          setError(extractApiError(e, "Erro ao excluir frequência."));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleGerarRelatorioCarradas = async () => {
    if (!relDataInicial || !relDataFinal) {
      setError("Informe o período do relatório.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetchRelatorioCarradas({
        dataInicial: relDataInicial,
        dataFinal: relDataFinal,
        caminhaoId: relCaminhaoId || undefined,
        material: relMaterial || undefined,
      });
      setRelatorioCarradas(r);
      setPrintGeneratedAt(null);
    } catch (e) {
      setError(extractApiError(e, "Erro ao gerar relatório de carradas."));
    } finally {
      setBusy(false);
    }
  };

  const exportRelatorioPdf = async () => {
    const generatedAt = new Date().toISOString();
    flushSync(() => setPrintGeneratedAt(generatedAt));
    await waitForPrintableImages();
    window.print();
  };

  const serieDiariaChart = useMemo(
    () =>
      (resumo?.serieProducaoDiaria ?? []).map((p) => ({
        ...p,
        label: p.data.slice(5),
      })),
    [resumo],
  );

  const valorDiariasRelatorio =
    typeof relatorio?.indicadores.valorDiarias === "number" && Number.isFinite(relatorio.indicadores.valorDiarias)
      ? relatorio.indicadores.valorDiarias
      : 0;

  return (
    <div className={locacoesTabContentClass}>
      <div className="no-print flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} type="button" className={locacoesTabClass(subTab === id)} onClick={() => setSubTab(id)}>
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading && !resumo ? (
        <p className="text-sm text-slate-500">Carregando Carradas…</p>
      ) : (
        <>
          {subTab === "dashboard" && resumo && (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <LocacoesStatCard
                  title="Total caminhões"
                  value={resumo.totalCaminhoes}
                  icon={<Truck className="h-5 w-5" />}
                  accentClass="border-l-4 border-l-pink-400"
                />
                <LocacoesStatCard
                  title="Motoristas"
                  value={resumo.totalMotoristas}
                  icon={<Users className="h-5 w-5" />}
                  accentClass="border-l-4 border-l-blue-400"
                />
                <LocacoesStatCard
                  title="Carradas hoje"
                  value={resumo.carradasHoje}
                  icon={<Truck className="h-5 w-5" />}
                  accentClass="border-l-4 border-l-amber-400"
                />
                <LocacoesStatCard
                  title="Apontadores ativos"
                  value={resumo.apontadoresAtivos}
                  icon={<UserCheck className="h-5 w-5" />}
                  accentClass="border-l-4 border-l-emerald-400"
                />
                <LocacoesStatCard
                  title="Destaque do dia"
                  value={
                    resumo.caminhaoDestaqueHoje
                      ? `${resumo.caminhaoDestaqueHoje.caminhaoNome} (${resumo.caminhaoDestaqueHoje.quantidade})`
                      : "—"
                  }
                  icon={<Truck className="h-5 w-5" />}
                  accentClass="border-l-4 border-l-brand-500"
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
                  <h3 className="mb-3 text-sm font-semibold text-slate-700">Produção diária (30 dias)</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={serieDiariaChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="quantidade" name="Carradas" stroke="#9f1239" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
                  <h3 className="mb-3 text-sm font-semibold text-slate-700">Carradas por caminhão (30 dias)</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={resumo.serieCarradasPorCaminhao.slice(0, 8)}
                        margin={{ top: 4, right: 8, left: 0, bottom: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="caminhaoNome"
                          tick={{ fontSize: 9, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          angle={-25}
                          textAnchor="end"
                        />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="total" name="Total" fill="#9f1239" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {subTab === "apontadores" && (
            <div className="flex flex-col gap-6">
              <LocacoesSubToolbar
                title="Apontadores"
                end={
                  <Button
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setEditingApontador(null);
                      setModalApontadorOpen(true);
                    }}
                  >
                    Novo apontador
                  </Button>
                }
              >
                <div className="max-w-sm">
                  <Input
                    label="Buscar (nome ou CPF)"
                    value={apontadorBusca}
                    onChange={(e) => setApontadorBusca(e.target.value)}
                  />
                </div>
              </LocacoesSubToolbar>

              <div className={locacoesTableWrapClass}>
                <table className="min-w-full text-sm">
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-4 py-3">Nome</th>
                      <th className="px-4 py-3">CPF</th>
                      <th className="px-4 py-3">Telefone</th>
                      <th className="px-4 py-3">Tipo</th>
                      <th className="px-4 py-3">Função</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {apontadoresFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                          Nenhum apontador cadastrado.
                        </td>
                      </tr>
                    ) : (
                      apontadoresFiltrados.map((a) => (
                        <tr key={a.id} className="hover:bg-slate-50/80">
                          <td className="px-4 py-3 font-medium text-slate-800">{a.nome}</td>
                          <td className="px-4 py-3 text-slate-600">{formatCpfDisplay(a.cpf)}</td>
                          <td className="px-4 py-3 text-slate-600">{a.telefone || "—"}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {APONTADOR_TIPO_LABELS[a.tipoApontador === "jazida" ? "jazida" : "obra"]}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{a.funcao || "—"}</td>
                          <td className="px-4 py-3">
                            <span
                              className={
                                a.status === "ativo"
                                  ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                  : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                              }
                            >
                              {a.status === "ativo" ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                leftIcon={<Pencil className="h-3.5 w-3.5" />}
                                disabled={busy}
                                onClick={() => {
                                  setEditingApontador(a);
                                  setModalApontadorOpen(true);
                                }}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                                disabled={busy}
                                className="text-red-600"
                                onClick={() => handleDeleteApontador(a)}
                              >
                                Excluir
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "frequencia" && (
            <div className="flex flex-col gap-6">
              <LocacoesSubToolbar
                title="Frequência diária"
                end={
                  <Button onClick={() => void handleSaveFrequencia()} loading={busy} disabled={freqRows.length === 0}>
                    Salvar frequência
                  </Button>
                }
              >
                <div className="max-w-xs">
                  <DateInput label="Data" value={freqData} onChange={setFreqData} />
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Somente apontadores ativos. Um registro por apontador/dia (upsert).
                  Informe a diária acordada para manter o histórico de cada dia.
                </p>
              </LocacoesSubToolbar>

              <div className={locacoesTableWrapClass}>
                <table className="min-w-full text-sm">
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-4 py-3">Apontador</th>
                      <th className="px-4 py-3">Situação</th>
                      <th className="px-4 py-3">Valor da diária</th>
                      <th className="px-4 py-3">Observação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {freqRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                          Nenhum apontador ativo. Cadastre apontadores na sub-aba correspondente.
                        </td>
                      </tr>
                    ) : (
                      freqRows.map((row) => (
                        <tr key={row.apontadorId}>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.nome}</td>
                          <td className="px-4 py-3">
                            <Select
                              options={FREQUENCIA_SITUACOES.map((s) => ({
                                value: s,
                                label: FREQUENCIA_SITUACAO_LABELS[s],
                              }))}
                              value={row.situacao}
                              onChange={(e) => {
                                const situacao = e.target.value as FrequenciaSituacao;
                                setFreqRows((rows) =>
                                  rows.map((r) =>
                                    r.apontadorId === row.apontadorId ? { ...r, situacao } : r,
                                  ),
                                );
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              aria-label={`Valor da diária de ${row.nome}`}
                              placeholder="R$ 0,00"
                              className="w-36 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm tabular-nums"
                              value={row.valorDiaria ?? ""}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const valorDiaria = raw === "" ? null : Number(raw);
                                setFreqRows((rows) =>
                                  rows.map((r) =>
                                    r.apontadorId === row.apontadorId
                                      ? { ...r, valorDiaria: Number.isFinite(valorDiaria) ? valorDiaria : null }
                                      : r,
                                  ),
                                );
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                              value={row.observacao}
                              onChange={(e) => {
                                const observacao = upperLocacaoText(e.target.value);
                                setFreqRows((rows) =>
                                  rows.map((r) =>
                                    r.apontadorId === row.apontadorId ? { ...r, observacao } : r,
                                  ),
                                );
                              }}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "lancamentos" && (
            <div className="flex flex-col gap-6">
              <LocacoesSubToolbar
                title="Lançamentos de carradas"
                end={
                  <Button
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setEditingCarrada(null);
                      setModalCarradaOpen(true);
                    }}
                    disabled={contratosCarradas.length === 0}
                  >
                    Nova carrada
                  </Button>
                }
              >
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <DateInput label="Data inicial" value={lancDataInicial} onChange={setLancDataInicial} />
                  <DateInput label="Data final" value={lancDataFinal} onChange={setLancDataFinal} />
                  <Select
                    label="Contrato"
                    options={[
                      { value: "", label: "Todos" },
                      ...contratosCarradas.map((c) => ({
                        value: c.id,
                        label: `${c.codigoContrato} · ${c.clienteSiengeNome}`,
                      })),
                    ]}
                    value={lancContratoId}
                    onChange={(e) => setLancContratoId(e.target.value)}
                  />
                  <Select
                    label="Caminhão"
                    options={[
                      { value: "", label: "Todos" },
                      ...caminhoes.map((c) => ({ value: c.id, label: c.nome })),
                    ]}
                    value={lancCaminhaoId}
                    onChange={(e) => setLancCaminhaoId(e.target.value)}
                  />
                  <div className="flex items-end">
                    <Button variant="secondary" onClick={() => void loadLancamentos()} disabled={busy}>
                      Aplicar
                    </Button>
                  </div>
                </div>
                {contratosCarradas.length === 0 && (
                  <p className="mt-2 text-sm text-amber-700">
                    Cadastre um contrato do modelo &quot;Locação por carradas&quot; em Contratos para liberar
                    lançamentos.
                  </p>
                )}
              </LocacoesSubToolbar>

              <div className={locacoesTableWrapClass}>
                <table className="min-w-full text-sm">
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-4 py-3">Data</th>
                      <th className="px-4 py-3">Contrato</th>
                      <th className="px-4 py-3">Máquina</th>
                      <th className="px-4 py-3">Material</th>
                      <th className="px-4 py-3">Qtd</th>
                      <th className="px-4 py-3">Vl. unit.</th>
                      <th className="px-4 py-3">Total</th>
                      <th className="px-4 py-3">Anexos</th>
                      <th className="px-4 py-3">Operador</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lancamentos.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                          Nenhum lançamento no período.
                        </td>
                      </tr>
                    ) : (
                      lancamentos.map((c) => {
                        const contrato = contratosById.get(c.contratoId);
                        const fotos = c.fotos ?? [];
                        const total = c.quantidade * (c.valorUnitario || 0);
                        return (
                          <tr key={c.id} className="hover:bg-slate-50/80">
                            <td className="px-4 py-3 text-slate-700">{formatDate(c.data)}</td>
                            <td className="px-4 py-3 text-slate-700">
                              {contrato
                                ? `${contrato.codigoContrato} · ${contrato.clienteSiengeNome}`
                                : c.contratoId || "—"}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {caminhoesById.get(c.caminhaoId)?.nome ?? c.caminhaoId}
                            </td>
                            <td className="px-4 py-3 text-slate-700">{c.material || "—"}</td>
                            <td className="px-4 py-3 text-slate-700">{c.quantidade}</td>
                            <td className="px-4 py-3 tabular-nums text-slate-700">
                              {c.valorUnitario > 0 ? `R$ ${formatNumber(c.valorUnitario, 2)}` : "—"}
                            </td>
                            <td className="px-4 py-3 tabular-nums font-medium text-slate-800">
                              {total > 0 ? `R$ ${formatNumber(total, 2)}` : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {fotos.length === 0 ? (
                                <span className="text-amber-700">Sem foto</span>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                                  onClick={() =>
                                    setFotoPreview({
                                      items: fotos.map((f) => ({ carradaId: c.id, fotoId: f.id })),
                                      index: 0,
                                    })
                                  }
                                >
                                  <Camera className="h-3.5 w-3.5" />
                                  {fotos.length}
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{c.motorista || "—"}</td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leftIcon={<Pencil className="h-3.5 w-3.5" />}
                                  disabled={busy}
                                  onClick={() => {
                                    setEditingCarrada(c);
                                    setModalCarradaOpen(true);
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                                  className="text-red-600"
                                  disabled={busy}
                                  onClick={() => handleDeleteCarrada(c)}
                                >
                                  Excluir
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "relatorio" && (
            <div className="flex flex-col gap-6">
              <div className="no-print flex flex-wrap gap-2 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  className={locacoesTabClass(relKind === "carradas")}
                  onClick={() => setRelKind("carradas")}
                >
                  <Truck className="h-4 w-4" />
                  Relatório de Carradas
                </button>
                <button
                  type="button"
                  className={locacoesTabClass(relKind === "presenca")}
                  onClick={() => setRelKind("presenca")}
                >
                  <ClipboardList className="h-4 w-4" />
                  Relatório de Presença
                </button>
              </div>

              {relKind === "carradas" && (
                <>
                  <LocacoesSubToolbar
                    className="no-print"
                    title="Relatório de carradas"
                    end={
                      <div className="flex gap-2">
                        <Button onClick={() => void handleGerarRelatorioCarradas()} loading={busy}>
                          Gerar
                        </Button>
                        <Button
                          variant="secondary"
                          leftIcon={<Printer className="h-4 w-4" />}
                          disabled={!relatorioCarradas}
                          onClick={exportRelatorioPdf}
                        >
                          Imprimir / PDF
                        </Button>
                      </div>
                    }
                  >
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <Select
                        label="Máquina / Placa"
                        options={[
                          { value: "", label: "Todas as máquinas" },
                          ...caminhoes.map((c) => ({
                            value: c.id,
                            label: `${c.nome}${c.placa ? ` · ${c.placa}` : ""}`,
                          })),
                        ]}
                        value={relCaminhaoId}
                        onChange={(e) => setRelCaminhaoId(e.target.value)}
                      />
                      <Select
                        label="Material"
                        options={[
                          { value: "", label: "Todos os materiais" },
                          ...CARRADA_MATERIAIS.map((m) => ({ value: m, label: m })),
                        ]}
                        value={relMaterial}
                        onChange={(e) => setRelMaterial(e.target.value)}
                      />
                      <DateInput label="Data inicial" value={relDataInicial} onChange={setRelDataInicial} />
                      <DateInput label="Data final" value={relDataFinal} onChange={setRelDataFinal} />
                    </div>
                  </LocacoesSubToolbar>

                  {relatorioCarradas && (
                    <div className="locacoes-report-print rounded-xl border border-slate-200 bg-white shadow-card">
                      <header className="print-only locacoes-report-print-header">
                        <div className="locacoes-report-print-brand">
                          <div className="locacoes-report-print-logo" aria-hidden="true">
                            S
                          </div>
                          <div>
                            <p className="locacoes-report-print-company">{APP_SHORT_NAME} Engenharia</p>
                            <p className="locacoes-report-print-module">
                              Semog Locações — Relatório de Apropriação
                            </p>
                          </div>
                        </div>
                        <div className="locacoes-report-print-meta">
                          <p>
                            <strong>Gerado em:</strong>{" "}
                            {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
                          </p>
                          <p>
                            <strong>Período:</strong> {formatDate(relatorioCarradas.dataInicial)} –{" "}
                            {formatDate(relatorioCarradas.dataFinal)}
                          </p>
                          {relatorioCarradas.material && (
                            <p>
                              <strong>Material:</strong> {relatorioCarradas.material}
                            </p>
                          )}
                          {relCaminhaoId && caminhoesById.get(relCaminhaoId) && (
                            <p>
                              <strong>Máquina:</strong> {caminhoesById.get(relCaminhaoId)!.nome}
                              {caminhoesById.get(relCaminhaoId)!.placa
                                ? ` · ${caminhoesById.get(relCaminhaoId)!.placa}`
                                : ""}
                            </p>
                          )}
                        </div>
                      </header>

                      <div className="no-print flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                          <BarChart3 className="h-4 w-4 text-brand-600" />
                          Carradas — {formatDate(relatorioCarradas.dataInicial)} a{" "}
                          {formatDate(relatorioCarradas.dataFinal)}
                          {relatorioCarradas.material ? ` · ${relatorioCarradas.material}` : ""}
                        </h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Printer className="h-3.5 w-3.5" />}
                          onClick={exportRelatorioPdf}
                        >
                          PDF (imprimir)
                        </Button>
                      </div>

                      <div className="no-print grid grid-cols-2 gap-3 px-4 py-3 sm:grid-cols-5">
                        <LocacoesStatCard
                          title="Lançamentos"
                          value={relatorioCarradas.indicadores.totalLancamentos}
                          icon={<ClipboardList className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-emerald-400"
                        />
                        <LocacoesStatCard
                          title="Quantidade (carradas)"
                          value={relatorioCarradas.indicadores.totalCarradas}
                          icon={<Truck className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-blue-400"
                        />
                        <LocacoesStatCard
                          title="Máquinas"
                          value={relatorioCarradas.indicadores.totalMaquinas}
                          icon={<Truck className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-amber-400"
                        />
                        <LocacoesStatCard
                          title="Anexos"
                          value={relatorioCarradas.indicadores.totalAnexos}
                          icon={<Paperclip className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-sky-400"
                        />
                        <LocacoesStatCard
                          title="Total a pagar"
                          value={`R$ ${formatNumber(relatorioCarradas.indicadores.valorTotal, 2)}`}
                          icon={<BarChart3 className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-brand-400"
                        />
                      </div>

                      <div className="locacoes-report-print-body px-4 py-3">
                        <h1 className="print-only locacoes-report-print-title">Relatório de Carradas</h1>
                        <p className="print-only locacoes-report-print-period">
                          Período: {formatDate(relatorioCarradas.dataInicial)} a{" "}
                          {formatDate(relatorioCarradas.dataFinal)}
                          {relatorioCarradas.material ? ` • Material: ${relatorioCarradas.material}` : ""}
                          {relCaminhaoId && caminhoesById.get(relCaminhaoId)
                            ? ` • Máquina: ${caminhoesById.get(relCaminhaoId)!.nome}`
                            : ""}
                        </p>

                        <section>
                          <h2 className="print-only locacoes-report-section-title">Indicadores</h2>
                          <div className="print-only locacoes-report-table-wrap">
                            <table className="locacoes-report-table min-w-full text-sm">
                              <thead>
                                <tr>
                                  <th>Lançamentos</th>
                                  <th>Quantidade</th>
                                  <th>Máquinas</th>
                                  <th>Anexos</th>
                                  <th>Total a pagar</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{relatorioCarradas.indicadores.totalLancamentos}</td>
                                  <td>{relatorioCarradas.indicadores.totalCarradas}</td>
                                  <td>{relatorioCarradas.indicadores.totalMaquinas}</td>
                                  <td>{relatorioCarradas.indicadores.totalAnexos}</td>
                                  <td>R$ {formatNumber(relatorioCarradas.indicadores.valorTotal, 2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </section>

                        <section className="print-only">
                          <h2 className="locacoes-report-section-title">Resumo por caminhão</h2>
                          <div className="locacoes-report-table-wrap">
                            <table className="locacoes-report-table min-w-full text-sm">
                              <thead>
                                <tr>
                                  <th>Caminhão</th>
                                  <th>Placa</th>
                                  <th>Carradas</th>
                                  <th>Total a pagar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {relatorioCarradas.resumoPorCaminhao.map((caminhao) => (
                                  <tr key={caminhao.maquinaId}>
                                    <td>{caminhao.maquinaNome}</td>
                                    <td>{caminhao.placa || "—"}</td>
                                    <td>{caminhao.totalCarradas}</td>
                                    <td>R$ {formatNumber(caminhao.valorTotal, 2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </section>

                        <section>
                          <h2 className="print-only locacoes-report-section-title">Detalhamento</h2>
                          <div className="locacoes-report-table-wrap overflow-x-auto">
                            <table className="locacoes-report-table min-w-full text-sm">
                              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-4 py-3">Data</th>
                                  <th className="px-4 py-3">Máquina</th>
                                  <th className="px-4 py-3">Operador</th>
                                  <th className="px-4 py-3">Placa</th>
                                  <th className="px-4 py-3">Material</th>
                                  <th className="px-4 py-3 text-right">Jazida</th>
                                  <th className="px-4 py-3 text-right">Obra</th>
                                  <th className="px-4 py-3">Comparativo</th>
                                  <th className="px-4 py-3">Status</th>
                                  <th className="px-4 py-3 text-right">Valor/carrada</th>
                                  <th className="px-4 py-3 text-right">Total</th>
                                  <th className="px-4 py-3">Anexos</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {relatorioCarradas.detalhe.length === 0 ? (
                                  <tr>
                                    <td colSpan={12} className="px-4 py-6 text-center text-slate-500">
                                      Sem lançamentos no período.
                                    </td>
                                  </tr>
                                ) : (
                                  relatorioCarradas.detalhe.map((d) => (
                                    <tr key={d.id} className="hover:bg-slate-50/80">
                                      <td className="px-4 py-3 text-slate-700">{formatDate(d.data)}</td>
                                      <td className="px-4 py-3 font-medium text-slate-800">{d.maquinaNome}</td>
                                      <td className="px-4 py-3 text-slate-700">{d.operador || "—"}</td>
                                      <td className="px-4 py-3 text-slate-700">{d.placa || "—"}</td>
                                      <td className="px-4 py-3 text-slate-700">{d.material || "—"}</td>
                                      <td className="px-4 py-3 tabular-nums text-right text-slate-800">
                                        {d.comparativo === "manual" ? "—" : d.quantidadeJazida}
                                      </td>
                                      <td className="px-4 py-3 tabular-nums text-right text-slate-800">
                                        {d.comparativo === "manual" ? d.quantidade : d.quantidadeObra}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span
                                          className={cn(
                                            "inline-flex rounded-md px-2 py-0.5 text-xs font-semibold",
                                            d.comparativo === "igual" && "bg-emerald-100 text-emerald-800",
                                            d.comparativo === "obra_menor" && "bg-red-100 text-red-800",
                                            d.comparativo === "obra_maior" && "bg-sky-100 text-sky-800",
                                            d.comparativo === "incompleto" && "bg-amber-100 text-amber-800",
                                            d.comparativo === "manual" && "bg-slate-100 text-slate-700",
                                          )}
                                        >
                                          {COMPARATIVO_CARRADA_LABELS[d.comparativo] ?? d.comparativo}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-xs font-medium text-slate-600">
                                        {d.statusAprovacao === "aprovado"
                                          ? "Aprovado"
                                          : d.statusAprovacao === "rejeitado"
                                            ? "Rejeitado"
                                            : "Aguardando"}
                                      </td>
                                      <td className="px-4 py-3 tabular-nums text-right text-slate-800">
                                        R$ {formatNumber(d.valorUnitario, 2)}
                                      </td>
                                      <td className="px-4 py-3 tabular-nums text-right font-medium text-slate-800">
                                        {d.statusAprovacao === "aprovado"
                                          ? `R$ ${formatNumber(d.valorTotal, 2)}`
                                          : "—"}
                                      </td>
                                      <td className="px-4 py-3">
                                        {d.anexos.length === 0 ? (
                                          <span className="text-slate-400">—</span>
                                        ) : (
                                          <>
                                            <span className="print-only">{d.anexos.length}</span>
                                            <div className="no-print flex flex-wrap items-center gap-1">
                                              {d.anexos.map((foto) => (
                                                <button
                                                  key={foto.id}
                                                  type="button"
                                                  title={foto.originalName || "Anexo"}
                                                  className="rounded p-0.5 text-sky-600 hover:bg-sky-50 hover:text-sky-800"
                                                  onClick={() =>
                                                    setFotoPreview({
                                                      items: d.anexos.map((a) => ({
                                                        carradaId:
                                                          a.lancamentoId ??
                                                          d.lancamentoIds?.[0] ??
                                                          d.id,
                                                        fotoId: a.id,
                                                      })),
                                                      index: Math.max(
                                                        0,
                                                        d.anexos.findIndex((a) => a.id === foto.id),
                                                      ),
                                                    })
                                                  }
                                                >
                                                  <Paperclip className="h-4 w-4" />
                                                </button>
                                              ))}
                                            </div>
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </section>

                        {relatorioCarradas.indicadores.totalAnexos > 0 && (
                          <section className="locacoes-report-attachments">
                            <h2 className="locacoes-report-section-title">Fotos dos serviços realizados</h2>
                            {relatorioCarradas.detalhe
                              .filter((d) => d.anexos.length > 0)
                              .map((d) => (
                                <div key={d.id} className="locacoes-report-attachment-group">
                                  <p className="locacoes-report-attachment-caption">
                                    {formatDate(d.data)} — {d.maquinaNome}
                                    {d.operador ? ` — ${d.operador}` : ""}
                                    {d.placa ? ` (${d.placa})` : ""}
                                  </p>
                                  <div className="locacoes-report-photo-grid">
                                    {d.anexos.map((foto) => (
                                      <figure key={foto.id} className="locacoes-report-photo">
                                        <img
                                          src={carradaFotoUrl(
                                            foto.lancamentoId ?? d.lancamentoIds?.[0] ?? d.id,
                                            foto.id,
                                          )}
                                          alt={`Anexo de ${d.maquinaNome} em ${formatDate(d.data)}`}
                                          loading="eager"
                                          decoding="sync"
                                        />
                                        <figcaption>{foto.originalName || "Anexo"}</figcaption>
                                      </figure>
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </section>
                        )}
                      </div>

                      <p className="no-print border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                        {relatorioCarradas.detalhe.length} lançamento(s) • Período{" "}
                        {formatDate(relatorioCarradas.dataInicial)} – {formatDate(relatorioCarradas.dataFinal)}
                      </p>

                      <footer className="print-only locacoes-report-print-footer">
                        {relatorioCarradas.detalhe.length} lançamento(s) • {APP_SHORT_NAME} Locações —
                        Apropriação • Impresso em{" "}
                        {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
                      </footer>
                    </div>
                  )}
                </>
              )}

              {relKind === "presenca" && (
                <>
                  <LocacoesSubToolbar
                    className="no-print"
                    title="Relatório de frequência"
                    end={
                      <div className="flex gap-2">
                        <Button onClick={() => void handleGerarRelatorioPresenca()} loading={busy}>
                          Gerar
                        </Button>
                        <Button
                          variant="secondary"
                          leftIcon={<Printer className="h-4 w-4" />}
                          disabled={!relatorio}
                          onClick={exportRelatorioPdf}
                        >
                          Imprimir / PDF
                        </Button>
                      </div>
                    }
                  >
                    <div className="grid gap-3 sm:grid-cols-3">
                      <Select
                        label="Apontador"
                        options={apontadores.map((a) => ({ value: a.id, label: a.nome }))}
                        value={relApontadorId}
                        onChange={(e) => setRelApontadorId(e.target.value)}
                        placeholder="Selecione"
                      />
                      <DateInput label="Data inicial" value={relDataInicial} onChange={setRelDataInicial} />
                      <DateInput label="Data final" value={relDataFinal} onChange={setRelDataFinal} />
                    </div>
                  </LocacoesSubToolbar>

                  {relatorio && (
                    <div className="locacoes-report-print rounded-xl border border-slate-200 bg-white shadow-card">
                      <header className="print-only locacoes-report-print-header">
                        <div className="locacoes-report-print-brand">
                          <div className="locacoes-report-print-logo" aria-hidden="true">
                            S
                          </div>
                          <div>
                            <p className="locacoes-report-print-company">{APP_SHORT_NAME} Engenharia</p>
                            <p className="locacoes-report-print-module">
                              Semog Locações — Relatório de Apropriação
                            </p>
                          </div>
                        </div>
                        <div className="locacoes-report-print-meta">
                          <p>
                            <strong>Gerado em:</strong>{" "}
                            {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
                          </p>
                          <p>
                            <strong>Período:</strong> {formatDate(relatorio.dataInicial)} –{" "}
                            {formatDate(relatorio.dataFinal)}
                          </p>
                          <p>
                            <strong>Apontador:</strong> {relatorio.apontador.nome}
                          </p>
                        </div>
                      </header>

                      <div className="no-print flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                          <ClipboardList className="h-4 w-4 text-brand-600" />
                          Frequência — {relatorio.apontador.nome} ({formatDate(relatorio.dataInicial)} a{" "}
                          {formatDate(relatorio.dataFinal)})
                        </h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Printer className="h-3.5 w-3.5" />}
                          onClick={exportRelatorioPdf}
                        >
                          PDF (imprimir)
                        </Button>
                      </div>

                      <div className="no-print grid grid-cols-2 gap-3 px-4 py-3 sm:grid-cols-5">
                        <LocacoesStatCard
                          title="Dias trabalhados"
                          value={relatorio.indicadores.diasTrabalhados}
                          icon={<UserCheck className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-emerald-400"
                        />
                        <LocacoesStatCard
                          title="% presença"
                          value={`${relatorio.indicadores.percentualPresenca}%`}
                          icon={<ClipboardList className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-blue-400"
                        />
                        <LocacoesStatCard
                          title="Faltas"
                          value={relatorio.indicadores.faltas}
                          icon={<Users className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-red-400"
                        />
                        <LocacoesStatCard
                          title="Meio período"
                          value={relatorio.indicadores.meioPeriodo}
                          icon={<Users className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-amber-400"
                        />
                        <LocacoesStatCard
                          title="Total de diárias"
                          value={`R$ ${formatNumber(valorDiariasRelatorio, 2)}`}
                          icon={<BarChart3 className="h-5 w-5" />}
                          accentClass="border-l-4 border-l-brand-400"
                        />
                      </div>

                      <p className="no-print px-4 text-xs text-slate-500">
                        Fórmula: dias trabalhados = presentes + meio período; % = round(((presentes + meio período ×
                        0,5) / total) × 100).
                      </p>

                      <div className="locacoes-report-print-body px-4 py-3">
                        <h1 className="print-only locacoes-report-print-title">Relatório de Presença</h1>
                        <p className="print-only locacoes-report-print-period">
                          Apontador: {relatorio.apontador.nome} • Período: {formatDate(relatorio.dataInicial)} a{" "}
                          {formatDate(relatorio.dataFinal)}
                        </p>

                        <section>
                          <h2 className="print-only locacoes-report-section-title">Indicadores</h2>
                          <div className="print-only locacoes-report-table-wrap">
                            <table className="locacoes-report-table min-w-full text-sm">
                              <thead>
                                <tr>
                                  <th>Dias trabalhados</th>
                                  <th>% presença</th>
                                  <th>Faltas</th>
                                  <th>Meio período</th>
                                  <th>Presentes</th>
                                  <th>Folgas</th>
                                  <th>Atestados</th>
                                  <th>Total de diárias</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{relatorio.indicadores.diasTrabalhados}</td>
                                  <td>{relatorio.indicadores.percentualPresenca}%</td>
                                  <td>{relatorio.indicadores.faltas}</td>
                                  <td>{relatorio.indicadores.meioPeriodo}</td>
                                  <td>{relatorio.indicadores.presentes}</td>
                                  <td>{relatorio.indicadores.folgas}</td>
                                  <td>{relatorio.indicadores.atestados}</td>
                                  <td>R$ {formatNumber(valorDiariasRelatorio, 2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </section>

                        <section>
                          <h2 className="print-only locacoes-report-section-title">Detalhamento</h2>
                          <div className="locacoes-report-table-wrap overflow-x-auto">
                            <table className="locacoes-report-table min-w-full text-sm">
                              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-4 py-3">Data</th>
                                  <th className="px-4 py-3">Situação</th>
                                  <th className="px-4 py-3">Valor da diária</th>
                                  <th className="px-4 py-3">Observação</th>
                                  <th className="no-print px-4 py-3 text-right">Ações</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {relatorio.detalhe.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                                      Sem registros no período.
                                    </td>
                                  </tr>
                                ) : (
                                  relatorio.detalhe.map((d) => (
                                    <tr key={d.id} className="hover:bg-slate-50/80">
                                      <td className="px-4 py-3">{formatDate(d.data)}</td>
                                      <td className="px-4 py-3">{FREQUENCIA_SITUACAO_LABELS[d.situacao]}</td>
                                      <td className="px-4 py-3 tabular-nums text-slate-700">
                                        {typeof d.valorDiaria === "number" && Number.isFinite(d.valorDiaria)
                                          ? `R$ ${formatNumber(d.valorDiaria, 2)}`
                                          : "—"}
                                      </td>
                                      <td className="px-4 py-3 text-slate-500">{d.observacao || "—"}</td>
                                      <td className="no-print px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<Pencil className="h-3.5 w-3.5" />}
                                            disabled={busy}
                                            onClick={() => handleEditFrequencia(d)}
                                          >
                                            Editar
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                                            className="text-red-600"
                                            disabled={busy}
                                            onClick={() => handleDeleteFrequencia(d)}
                                          >
                                            Excluir
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </section>
                      </div>

                      <p className="no-print border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                        {relatorio.detalhe.length} registro(s) • {relatorio.apontador.nome} • Período{" "}
                        {formatDate(relatorio.dataInicial)} – {formatDate(relatorio.dataFinal)}
                      </p>

                      <footer className="print-only locacoes-report-print-footer">
                        {relatorio.detalhe.length} registro(s) • {APP_SHORT_NAME} Locações — Apropriação •
                        Impresso em {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
                      </footer>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      <ModalApontador
        open={modalApontadorOpen}
        editing={editingApontador}
        contratos={contratosCarradas}
        busy={busy}
        onClose={() => {
          setModalApontadorOpen(false);
          setEditingApontador(null);
        }}
        onSave={handleSaveApontador}
        onDelete={editingApontador ? () => handleDeleteApontador(editingApontador) : undefined}
      />

      <ModalCarrada
        open={modalCarradaOpen}
        editing={editingCarrada}
        contratos={contratosCarradas}
        caminhoes={caminhoes}
        busy={busy}
        onClose={() => {
          setModalCarradaOpen(false);
          setEditingCarrada(null);
          void loadLancamentos();
        }}
        onSave={handleSaveCarrada}
        onDelete={editingCarrada ? () => handleDeleteCarrada(editingCarrada) : undefined}
      />

      {fotoPreview && (
        <CarradaFotoLightbox
          items={fotoPreview.items}
          initialIndex={fotoPreview.index}
          onClose={() => setFotoPreview(null)}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(confirm)}
        title={confirm?.title ?? ""}
        message={confirm?.message ?? ""}
        confirmLabel="Confirmar"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const action = confirm?.action;
          setConfirm(null);
          if (action) void action();
        }}
      />
    </div>
  );
}
